import React from 'react';
import {
  Campaign,
  CampaignDelivery,
  CampaignDeliveryTarget,
  CampaignWorkshopMeeting,
} from '../../../../support/campaigns';
import {
  useFetchCampaigns,
  useFetchAllDeliveries,
  useFetchAllWorkshopMeetings,
  useFetchUserSalesLevels,
} from '../../../../hooks/campaign-hooks';
import useCampaignPipeline from '../../../../hooks/useCampaignPipeline';
import { AnalyticsScope } from '../../../../support/campaignAnalytics';
import { buildSalesLevelIndex } from '../../../../support/campaignSalesLevels';
import { BuilderRoot, SectionHeader, builderStyles } from '../builder/primitives';
import { Breadcrumbs, Crumb, dashboardCss as css } from './dashboardParts';
import CampaignView from './views/CampaignView';
import DeliveryView from './views/DeliveryView';
import TargetView from './views/TargetView';

/**
 * Management > Campaigns > Analytics — an interactive, enterprise master-detail dashboard.
 *
 * A single toolbar (campaign selector + drill breadcrumb) drives one drill path:
 *   Program → Campaign → Delivery → Customer/Opportunity.
 * Each level is its own view (`views/`), laid out as a KPI strip + a responsive chart grid + clickable
 * master tables. Picking a campaign from the selector jumps straight to it; clicking a row in a table
 * drills a level deeper; the breadcrumb walks back up. Charts are the registry's tiles (so a new
 * campaign type contributes analytics with a `registerCampaignAnalytics` call); the master tables are
 * the interactive navigation. Every level fetches its own data lazily — the page opens on one
 * campaigns + one deliveries query, and depth is paid for on demand.
 *
 * Data is aggregated client-side today from the generic delivery/campaign CRUD (an admin sees all
 * rows); when the server-side rollup endpoints land, only the fetching seam swaps.
 */
type Props = {
  /** When set, the dashboard opens locked to one campaign (per-campaign embed). */
  campaignId?: number;
  /** Reserved for a future single-campaign embed mode; program dashboard ignores it. */
  scope?: AnalyticsScope;
};

type Selection = {
  campaignId?: number;
  deliveryId?: number;
  target?: CampaignDeliveryTarget;
};

const ProgramAnalytics: React.FC<Props> = ({ campaignId }) => {
  const { data: campaignsRaw, isLoading: loadingCampaigns } = useFetchCampaigns();
  const { data: deliveriesRaw, isLoading: loadingDeliveries } = useFetchAllDeliveries();
  // Pipeline $ (Salesforce opportunity Amount) is the attribution anchor threaded through every view;
  // fetched once here (targets + per-opp amounts) and passed down so each level attributes dollars to
  // its sessions. Loading is intentionally NOT gated on this — the dashboard renders immediately and $
  // columns fill in as the opportunity fan-out resolves.
  const { targetsByDelivery, amountByOpp } = useCampaignPipeline();

  // Sales-level (Cisco territory) index for grouping enrollments/deliveries by SL1/SL2, and every
  // workshop's Webex meeting for deriving schedule status. Both are program-wide, fetched once and
  // threaded down; neither gates the initial render.
  const { data: usersRaw } = useFetchUserSalesLevels();
  const { data: meetingsRaw } = useFetchAllWorkshopMeetings();

  const campaigns = React.useMemo(() => (campaignsRaw ?? []) as Campaign[], [campaignsRaw]);
  const deliveries = React.useMemo(() => (deliveriesRaw ?? []) as CampaignDelivery[], [deliveriesRaw]);
  const userIndex = React.useMemo(() => buildSalesLevelIndex(usersRaw ?? []), [usersRaw]);
  const meetingsByDelivery = React.useMemo(() => {
    const m = new Map<number, CampaignWorkshopMeeting[]>();
    for (const mtg of (meetingsRaw ?? []) as CampaignWorkshopMeeting[]) {
      const list = m.get(mtg.delivery) ?? [];
      list.push(mtg);
      m.set(mtg.delivery, list);
    }
    return m;
  }, [meetingsRaw]);
  const isLoading = loadingCampaigns || loadingDeliveries;

  const [sel, setSel] = React.useState<Selection>({ campaignId });

  // The dashboard is always per-campaign — default to the requested campaign, else the first one. The
  // guard keeps a later user selection from being clobbered once something is selected.
  React.useEffect(() => {
    if (sel.campaignId == null && campaigns.length > 0) {
      setSel({ campaignId: campaignId ?? campaigns[0].id });
    }
  }, [sel.campaignId, campaigns, campaignId]);

  const campaign = React.useMemo(
    () => (sel.campaignId != null ? campaigns.find((c) => c.id === sel.campaignId) : undefined),
    [campaigns, sel.campaignId]
  );
  const campaignDeliveries = React.useMemo(
    () => (sel.campaignId != null ? deliveries.filter((d) => d.campaign === sel.campaignId) : []),
    [deliveries, sel.campaignId]
  );
  const delivery = React.useMemo(
    () => (sel.deliveryId != null ? campaignDeliveries.find((d) => d.id === sel.deliveryId) : undefined),
    [campaignDeliveries, sel.deliveryId]
  );

  // Navigation helpers.
  const goCampaign = (id: number) => setSel({ campaignId: id });
  const goDelivery = (id: number) => setSel((s) => ({ campaignId: s.campaignId, deliveryId: id }));
  const goTarget = (t: CampaignDeliveryTarget) => setSel((s) => ({ ...s, deliveryId: s.deliveryId, target: t }));

  // Breadcrumb path — starts at the campaign (there is no program-wide rollup view).
  const crumbs: Crumb[] = [];
  if (campaign) crumbs.push({ label: campaign.name, onClick: () => goCampaign(campaign.id) });
  if (delivery)
    crumbs.push({
      label: delivery.name ?? `Delivery #${delivery.id}`,
      onClick: () => goDelivery(delivery.id),
    });
  if (sel.target)
    crumbs.push({
      label: sel.target.customer_name ?? sel.target.customer ?? `Customer #${sel.target.id}`,
    });

  const onSelectCampaign = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v) setSel({ campaignId: Number(v) });
  };

  return (
    <BuilderRoot wide>
      <SectionHeader
        title="Campaign Analytics"
        subtitle="Per-campaign rollups with drill-down into deliveries and the specific customers and opportunities each one drives."
      />

      {isLoading && <p className={builderStyles.lead}>Loading analytics…</p>}

      {!isLoading && (
        <div className={css.dashboard}>
          <div className={css.toolbar}>
            <div className={css.toolbarField}>
              <span className={css.toolbarLabel}>Campaign</span>
              <select
                className={css.select}
                value={sel.campaignId ?? ''}
                onChange={onSelectCampaign}
                aria-label="Select campaign"
              >
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <Breadcrumbs items={crumbs} />
          </div>

          {campaigns.length === 0 ? (
            <p className={builderStyles.lead}>No campaigns yet.</p>
          ) : sel.target && delivery ? (
            <TargetView delivery={delivery} target={sel.target} />
          ) : delivery ? (
            <DeliveryView
              delivery={delivery}
              onSelectTarget={goTarget}
              targetsByDelivery={targetsByDelivery}
              amountByOpp={amountByOpp}
            />
          ) : campaign ? (
            <CampaignView
              campaign={campaign}
              deliveries={campaignDeliveries}
              targetsByDelivery={targetsByDelivery}
              amountByOpp={amountByOpp}
              userIndex={userIndex}
              meetingsByDelivery={meetingsByDelivery}
            />
          ) : (
            <p className={builderStyles.lead}>Loading campaign…</p>
          )}
        </div>
      )}
    </BuilderRoot>
  );
};

export default ProgramAnalytics;
